import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import PropTypes from 'prop-types'

import { cn } from '@/utils/helpers'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 font-medium uppercase tracking-wider w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-primary/25 bg-primary/10 text-primary [a&]:hover:bg-primary/15',
        secondary:
          'border-border bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/80',
        destructive:
          'border-destructive/30 bg-destructive/10 text-destructive [a&]:hover:bg-destructive/15 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        outline: 'border-border text-muted-foreground [a&]:hover:bg-accent',
        // Project-specific
        success: 'border-success/40 bg-success/15 text-foreground [a&]:hover:bg-success/20',
        warning: 'border-yellow-300 bg-yellow-100 text-yellow-800 [a&]:hover:bg-yellow-200',
      },
      // Project-specific
      size: {
        default: 'text-xs',
        sm: 'text-sm',
        md: 'text-md',
        lg: 'text-lg',
        xl: 'text-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Badge({ className, variant, size, asChild = false, ...props }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }

Badge.displayName = 'Badge'

Badge.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'default',
    'secondary',
    'destructive',
    'outline',
    'success',
    'warning',
  ]),
  size: PropTypes.oneOf(['default', 'sm', 'md', 'lg', 'xl']),
  asChild: PropTypes.bool,
}
